
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Button, TextField, Typography, Paper } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { usePromptTemplateService, PromptTemplateType } from '../services/promptTemplateService';


const AdminPromptStructureFormPage: React.FC = () => {
	const navigate = useNavigate();
	const { id } = useParams<{ id?: string }>();
	const location = useLocation();
	const isCopy = new URLSearchParams(location.search).get('copy') === '1';
	const isNumericId = id !== undefined && !Number.isNaN(Number(id));
	const isEdit = isNumericId && !isCopy;	const { createPromptTemplate, updatePromptTemplate, fetchPromptTemplate } = usePromptTemplateService();
	const [form, setForm] = useState({
		title: '',
		description: '',
		intro: '',
		outro: '',
		persona: '',
		company: '',
		scenario: '',
	});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	function handleBack() {
		navigate('/admin/prompt-structures');
	}

	function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
		setForm({ ...form, [e.target.name]: e.target.value });
	}

	useEffect(() => {
	if ((isEdit || isCopy) && isNumericId) {
		setLoading(true);
		fetchPromptTemplate(Number(id))
		.then((data: PromptTemplateType) => {
			setForm({
			title: isCopy ? `${data.title} (Copy)` : data.title || '',
			description: data.description || '',
			intro: data.intro || '',
			outro: data.outro || '',
			persona: data.persona || '',
			company: data.company || '',
			scenario: data.scenario || '',
			});
		})
		.catch((e) => setError(e.message || 'Failed to load prompt structure'))
		.finally(() => setLoading(false));
	}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, isCopy, isNumericId]);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setLoading(true);
		setError(null);
		try {
			if (isEdit && id) {
			await updatePromptTemplate(Number(id), form);
			} else {
			await createPromptTemplate(form); // used for both create and copy
			}
			navigate('/admin/prompt-structures');
		} catch (err: any) {
			setError(err.message || 'Failed to save prompt structure');
		} finally {
			setLoading(false);
		}
	}

	return (
		<Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 8, p: 4, overflow: 'scroll' }}>
			<Paper sx={{ width: { xs: '95%', md: '70%', lg: '60%', xl: '45%' }, p: 4, borderRadius: 3, height: 'fit-content' }}>
				<PageHeader
					title={isEdit ? 'Edit Prompt Structure' : 'Create Prompt Structure'}
					subtitle={isEdit ? 'Edit the details below.' : 'Create a new prompt structure.'}
					onBack={handleBack}
					showBackButton={true}
				/>
				{error && isEdit ? (
					<Typography color="error" sx={{ mb: 2 }}>
						{error}
					</Typography>
					) : (
					<form onSubmit={handleSubmit}>
						<TextField
							label="Title"
							name="title"
							value={form.title}
							onChange={handleChange}
							fullWidth
							required
							sx={{ mb: 2 }}
							disabled={loading}
						/>
						<TextField
							label="Description"
							name="description"
							value={form.description}
							onChange={handleChange}
							fullWidth
							multiline
							minRows={2}
							sx={{ mb: 2 }}
							disabled={loading}
						/>
						<TextField
							label="Intro"
							name="intro"
							value={form.intro}
							onChange={handleChange}
							fullWidth
							multiline
							minRows={2}
							sx={{ mb: 2 }}
							disabled={loading}
						/>
						<TextField
							label="Persona"
							name="persona"
							value={form.persona}
							onChange={handleChange}
							fullWidth
							multiline
							sx={{ mb: 2 }}
							disabled={loading}
						/>
						<TextField
							label="Company"
							name="company"
							value={form.company}
							onChange={handleChange}
							fullWidth
							multiline
							sx={{ mb: 2 }}
							disabled={loading}
						/>
						<TextField
							label="Scenario"
							name="scenario"
							value={form.scenario}
							onChange={handleChange}
							fullWidth
							multiline
							sx={{ mb: 2 }}
							disabled={loading}
						/>
						<TextField
							label="Outro"
							name="outro"
							value={form.outro}
							onChange={handleChange}
							fullWidth
							multiline
							minRows={2}
							sx={{ mb: 2 }}
							disabled={loading}
						/>
						{error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
						<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
							<Button variant="outlined" onClick={handleBack} disabled={loading}>Cancel</Button>
							<Button variant="contained" type="submit" disabled={loading}>
								{loading ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save' : 'Create')}
							</Button>
						</Box>
					</form>
				)};
			</Paper>
		</Box>
	);
};

export default AdminPromptStructureFormPage;
